import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { contactInfo } from "../data";
import GalleryLightbox from "../components/GalleryLightbox";

export default function Workshop() {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hent værksteds-siden fra CMS
    fetch("https://api.dev.auxo.dk/public/by-slug/da/vaerkstedet")
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke hente værksteds-data");
        return res.json();
      })
      .then((data) => {
        setPageData(data);
        
        // Hent det tilknyttede galleri dynamisk baseret på gallery_slug
        const gSlug = data.gallery_slug || "galleri-vaerksted";
        return fetch(`https://api.dev.auxo.dk/public/by-slug/da/${gSlug}`)
          .then((res) => {
            if (!res.ok) {
              console.warn("Galleri blev ikke fundet i CMS, fortsætter uden galleri-sektion.");
              return null;
            }
            return res.json();
          })
          .catch(() => null);
      })
      .then((gData) => {
        if (gData) {
          setGalleryData(gData);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fejl ved hentning af værkstedsdata:", err);
        setLoading(false);
      });
  }, []);

  // Helpers
  const getVal = (field, defaultVal = "") => {
    if (!field) return defaultVal;
    if (typeof field === "object") {
      return field.da || Object.values(field)[0] || defaultVal;
    }
    return field;
  };

  const getResizedUrl = (url, width = 1200) => {
    if (!url) return "";
    const imgUrl = Array.isArray(url) ? url[0] : url;
    if (!imgUrl.startsWith("https://assets.auxodev.dk")) return imgUrl;
    return imgUrl.replace(
      "https://assets.auxodev.dk/",
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
    );
  };

  // Sæt metadata dynamisk
  useEffect(() => {
    if (pageData) {
      document.title = getVal(pageData.meta_title, "Værkstedet - Grønhøjauto v/Mads Kallerup");
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute(
        "content",
        getVal(
          pageData.meta_description,
          "Mød Mads Kallerup, din lokale mekaniker hos Grønhøjauto. Vi står for personlig service, ærligt arbejde og bred erfaring med alle bilmærker."
        )
      );
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-[120px]">
        <div className="text-center space-y-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-green" />
          <p className="text-stone-500 font-semibold">Indlæser værkstedet...</p>
        </div>
      </div>
    );
  }

  const heroImg = getResizedUrl(
    pageData?.hero_image || "https://assets.auxodev.dk/assets/45330f91-18ad-4301-b0b9-f03458bae5a3/gronhojauto-workshop-interior.jpg",
    1600
  );

  return (
    <div className="pt-[72px] min-[1200px]:pt-[115px]">
      {/* Hero / Banner */}
      <section className="relative py-20 bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt="Grønhøjauto Værksted"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-900/40 to-stone-950/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center space-y-4">
          <span className="text-xs font-bold tracking-widest text-brand-sage uppercase">
            Bag om Grønhøjauto
          </span>
          <h1 className="text-3xl font-black tracking-tight sm:text-4xl md:text-5xl text-white">
            {getVal(pageData?.title, "Værkstedet")}
          </h1>
          <p className="mx-auto max-w-2xl text-base text-stone-200 md:text-lg font-medium">
            {getVal(pageData?.subtitle, "Mød Mads Kallerup og hør historien bag Grønhøjauto")}
          </p>
        </div>
      </section>

      {/* Indhold Sektion */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          {/* Brødtekst fra CMS */}
          <div
            className="prose prose-stone max-w-none prose-p:text-stone-600 prose-p:leading-relaxed prose-p:text-base prose-li:text-stone-600 prose-strong:text-brand-stone-dark prose-headings:text-brand-stone-dark prose-headings:font-black space-y-6"
            dangerouslySetInnerHTML={{ __html: getVal(pageData?.content) }}
          />

          {/* Værkstedets faciliteter / USP'er */}
          <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-stone-200 bg-stone-50 p-6 space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green">
                <i className="fa-solid fa-screwdriver-wrench text-lg" />
              </div>
              <h3 className="font-bold text-brand-stone-dark text-base">Moderne udstyr</h3>
              <p className="text-sm text-stone-600">
                Vi råder over det nyeste test- og fejlsøgningsudstyr til hurtig og præcis diagnosticering.
              </p>
            </div>

            <div className="rounded-xl border border-stone-200 bg-stone-50 p-6 space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green">
                <i className="fa-solid fa-leaf text-lg" />
              </div>
              <h3 className="font-bold text-brand-stone-dark text-base">Miljøbevidst</h3>
              <p className="text-sm text-stone-600">
                Vi bortskaffer alle spildprodukter, dæk og batterier efter de strengeste miljøkrav.
              </p>
            </div>

            <div className="rounded-xl border border-stone-200 bg-stone-50 p-6 space-y-3 col-span-1 sm:col-span-2 lg:col-span-1">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-green/10 text-brand-green">
                <i className="fa-solid fa-mug-hot text-lg" />
              </div>
              <h3 className="font-bold text-brand-stone-dark text-base">Kaffe på kanden</h3>
              <p className="text-sm text-stone-600">
                Du er altid velkommen til at vente på værkstedet med en kop friskbrygget kaffe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Galleri Sektion */}
      {galleryData && galleryData.images && galleryData.images.length > 0 && (
        <section className="py-16 bg-brand-stone-light border-t border-stone-200/60">
          <div className="mx-auto max-w-5xl px-6 space-y-8">
            <div className="text-center space-y-2">
              <span className="text-xs font-bold tracking-widest text-brand-orange uppercase">
                Billedgallerier
              </span>
              <h2 className="text-2xl font-black tracking-tight text-brand-stone-dark sm:text-3xl">
                {getVal(galleryData.title, "Billeder fra værkstedet")}
              </h2>
              <p className="text-sm text-stone-500 max-w-xl mx-auto">
                {getVal(galleryData.description, "Klik på et billede for at se det i stor visning. Du kan swipe på mobil.")}
              </p>
            </div>

            {/* Lightbox Galleri */}
            <GalleryLightbox
              images={galleryData.images}
              title={getVal(galleryData.title, "Værkstedet")}
            />
          </div>
        </section>
      )}

      {/* CTA Sektion */}
      <section className="py-16 bg-brand-green text-white text-center space-y-6">
        <div className="mx-auto max-w-3xl px-6 space-y-4">
          <h2 className="text-2xl font-black tracking-tight sm:text-3xl">
            Skal vi kigge på din bil?
          </h2>
          <p className="text-stone-200 max-w-xl mx-auto text-sm sm:text-base">
            Uanset om det er en lille pære, der skal skiftes, eller det helt store serviceeftersyn, står Mads klar til at hjælpe dig.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              state={{ subject: "Book tid" }}
              className="rounded-xl bg-brand-orange px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-brand-orange-dark cursor-pointer"
            >
              Book tid nu
            </Link>
            <a
              href={`tel:+45${contactInfo.phone.replace(/\s/g, "")}`}
              className="rounded-xl bg-white/10 border border-white/20 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/20"
            >
              Ring: {contactInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
